/** @ken/module-inventory-management — frontend entry point.
 *
 * Owns the full stock-side flow per ADR-024: locations, balances, movements,
 * receiving, AND batches (the `inventory_batches` module was folded into this
 * one on 2026-05-27 per the same ADR — there is no separate batches module).
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { StockAdjustmentStorySearch } from "./routes/story/stock-adjustment-story-detail";
import type { StockTransferStorySearch } from "./routes/story/stock-transfer-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadArrivalStoryTabs = () => import("./routes/story/arrival-story-tabs");
const loadStockAdjustmentStoryTabs = () => import("./routes/story/stock-adjustment-story-tabs");
const loadStockTransferStoryTabs = () => import("./routes/story/stock-transfer-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const ArrivalCountPage = lazyScreen(
  () => import("./routes/arrival-count"),
  "ArrivalCountPage",
);
const ArrivalListPage = lazyScreen(
  () => import("./routes/arrival-list"),
  "ArrivalListPage",
);
const ShortDeliveriesPage = lazyScreen(
  () => import("./routes/short-deliveries"),
  "ShortDeliveriesPage",
);
const ArrivalStoryDetail = lazyScreen(
  () => import("./routes/story/arrival-story-detail"),
  "ArrivalStoryDetail",
);
const ArrivalActivityRoute = lazyScreen(
  loadArrivalStoryTabs,
  "ArrivalActivityRoute",
);
const ArrivalLinesRoute = lazyScreen(
  loadArrivalStoryTabs,
  "ArrivalLinesRoute",
);
const ArrivalOverviewRoute = lazyScreen(
  loadArrivalStoryTabs,
  "ArrivalOverviewRoute",
);
const ArrivalRelatedRoute = lazyScreen(
  loadArrivalStoryTabs,
  "ArrivalRelatedRoute",
);
const BatchListPage = lazyScreen(
  () => import("./routes/batch-list"),
  "BatchListPage",
);
const InventoryStockPage = lazyScreen(
  () => import("./routes/inventory-stock"),
  "InventoryStockPage",
);
const ReceivingCreatePage = lazyScreen(
  () => import("./routes/receiving-create"),
  "ReceivingCreatePage",
);
const ReceivingListPage = lazyScreen(
  () => import("./routes/receiving-list"),
  "ReceivingListPage",
);
const StockAdjustmentListPage = lazyScreen(
  () => import("./routes/stock-adjustment-list"),
  "StockAdjustmentListPage",
);
const StockTransferListPage = lazyScreen(
  () => import("./routes/stock-transfer-list"),
  "StockTransferListPage",
);
const StockAdjustmentStoryDetail = lazyScreen(
  () => import("./routes/story/stock-adjustment-story-detail"),
  "StockAdjustmentStoryDetail",
);
const StockAdjustmentActivityRoute = lazyScreen(
  loadStockAdjustmentStoryTabs,
  "StockAdjustmentActivityRoute",
);
const StockAdjustmentLinesRoute = lazyScreen(
  loadStockAdjustmentStoryTabs,
  "StockAdjustmentLinesRoute",
);
const StockAdjustmentOverviewRoute = lazyScreen(
  loadStockAdjustmentStoryTabs,
  "StockAdjustmentOverviewRoute",
);
const StockAdjustmentRelatedRoute = lazyScreen(
  loadStockAdjustmentStoryTabs,
  "StockAdjustmentRelatedRoute",
);
const StockTransferStoryDetail = lazyScreen(
  () => import("./routes/story/stock-transfer-story-detail"),
  "StockTransferStoryDetail",
);
const StockTransferActivityRoute = lazyScreen(
  loadStockTransferStoryTabs,
  "StockTransferActivityRoute",
);
const StockTransferLinesRoute = lazyScreen(
  loadStockTransferStoryTabs,
  "StockTransferLinesRoute",
);
const StockTransferOverviewRoute = lazyScreen(
  loadStockTransferStoryTabs,
  "StockTransferOverviewRoute",
);
const StockTransferRelatedRoute = lazyScreen(
  loadStockTransferStoryTabs,
  "StockTransferRelatedRoute",
);
export { ProductMovementsTab } from "./components/product-movements-tab";
export {
  QuantityChange,
  type QuantityBlock,
} from "./components/quantity-change";

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const stockRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory",
    component: InventoryStockPage,
  });

  const batchesRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/batches",
    // ADR-0122: the open batch + its active tab are URL-borne. `highlight` is
    // the same hand-off `entityRoute()` builds for every module list, so a
    // Related chip on a product links straight into the batch sheet; both
    // params survive a reload and a back-navigation.
    validateSearch: (raw: Record<string, unknown>): BatchListSearch => ({
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      ...(raw["batch_tab"] === "related" || raw["batch_tab"] === "activity"
        ? { batch_tab: raw["batch_tab"] as "related" | "activity" }
        : {}),
    }),
    component: BatchListPage,
  });

  const receivingListRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/receiving",
    // Goods arrival slice F (F.8): `?open=<id>` names the row whose side sheet
    // opens on load. No validateSearch: the page reads it with
    // useSearch({ strict: false }), and a receiving order id is a UUID — its
    // hyphens keep the default parser from turning it into anything but text.
    // (Left out on purpose: the initial bundle sits at its size gate.)
    component: ReceivingListPage,
  });

  const receivingCreateRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/receiving/new",
    component: ReceivingCreatePage,
  });

  // ── SMV-E: the two QuickBooks stock documents. Each is a list plus a story
  //    page (ADR-0081) — a `$id` shell, an index redirect to …/overview, and
  //    one child per tab, exactly as accounts_payable registers its two.
  // Every filter is URL-borne (ADR-0081 §3): a reload, a back-navigation from
  // a story page and a shared link all restore the same view. A key left out
  // of this list is a filter that silently resets.
  const stockListSearch = (raw: Record<string, unknown>): StockListSearch => {
    const out: StockListSearch = {};
    for (const key of [
      "q",
      "filter_status",
      "highlight",
      "when",
      "product_id",
      "site_id",
      "account_source_ref",
      "from_site_id",
      "to_site_id",
      "needs_attention",
    ] as const) {
      const value = raw[key];
      if (typeof value === "string" && value) out[key] = value;
    }
    // POP-01 (ADR-0229): the record open in the pop-up, so a reload reopens
    // it and the browser's back button closes it.
    if (typeof raw["open"] === "string" && raw["open"]) out.open = raw["open"];
    if (typeof raw["tab"] === "string" && raw["tab"]) out.tab = raw["tab"];
    return out;
  };

  const adjustmentsListRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/adjustments",
    validateSearch: stockListSearch,
    component: StockAdjustmentListPage,
  });

  const transfersListRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/transfers",
    validateSearch: stockListSearch,
    component: StockTransferListPage,
  });

  const storySearch = (
    raw: Record<string, unknown>,
  ): StockAdjustmentStorySearch => ({
    ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
    ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
      ? { filter_status: raw["filter_status"] }
      : {}),
    ...(typeof raw["from"] === "string" && raw["from"]
      ? { from: raw["from"] }
      : {}),
  });

  const adjustmentDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/adjustments/$id",
    validateSearch: storySearch,
    component: StockAdjustmentStoryDetail,
  });
  const adjustmentIndexRoute = createRoute({
    getParentRoute: () => adjustmentDetailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/inventory/adjustments/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });
  // Named, not inline: the cockpit's route-integrity check reads `const x =
  // createRoute(...)` declarations, so a tab declared inline is invisible to
  // it and every link to it reads as dead.
  const adjustmentOverviewRoute = createRoute({
    getParentRoute: () => adjustmentDetailRoute,
    path: "overview",
    component: StockAdjustmentOverviewRoute,
  });
  const adjustmentLinesRoute = createRoute({
    getParentRoute: () => adjustmentDetailRoute,
    path: "lines",
    component: StockAdjustmentLinesRoute,
  });
  const adjustmentRelatedRoute = createRoute({
    getParentRoute: () => adjustmentDetailRoute,
    path: "related",
    component: StockAdjustmentRelatedRoute,
  });
  const adjustmentActivityRoute = createRoute({
    getParentRoute: () => adjustmentDetailRoute,
    path: "activity",
    component: StockAdjustmentActivityRoute,
  });
  (
    adjustmentDetailRoute as unknown as { addChildren: (c: unknown) => void }
  ).addChildren([
    adjustmentIndexRoute,
    adjustmentOverviewRoute,
    adjustmentLinesRoute,
    adjustmentRelatedRoute,
    adjustmentActivityRoute,
  ]);

  const transferDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/transfers/$id",
    validateSearch: storySearch as (
      raw: Record<string, unknown>,
    ) => StockTransferStorySearch,
    component: StockTransferStoryDetail,
  });
  const transferIndexRoute = createRoute({
    getParentRoute: () => transferDetailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/inventory/transfers/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });
  const transferOverviewRoute = createRoute({
    getParentRoute: () => transferDetailRoute,
    path: "overview",
    component: StockTransferOverviewRoute,
  });
  const transferLinesRoute = createRoute({
    getParentRoute: () => transferDetailRoute,
    path: "lines",
    component: StockTransferLinesRoute,
  });
  const transferRelatedRoute = createRoute({
    getParentRoute: () => transferDetailRoute,
    path: "related",
    component: StockTransferRelatedRoute,
  });
  const transferActivityRoute = createRoute({
    getParentRoute: () => transferDetailRoute,
    path: "activity",
    component: StockTransferActivityRoute,
  });
  (
    transferDetailRoute as unknown as { addChildren: (c: unknown) => void }
  ).addChildren([
    transferIndexRoute,
    transferOverviewRoute,
    transferLinesRoute,
    transferRelatedRoute,
    transferActivityRoute,
  ]);

  // ── GA-E: goods arrival. The queue, the owner's money-first page, the Count
  //    screen (a full page of its own — a different mode on a different
  //    device, UI review §3) and the arrival story page (four routed tabs).
  //    Keyed by the BILL: an arrival is known by its bill before anybody has
  //    counted it.
  const arrivalListSearch = (
    raw: Record<string, unknown>,
  ): ArrivalListSearch => {
    const out: ArrivalListSearch = {};
    for (const key of [
      "state",
      "q",
      "supplier_id",
      "warehouse",
      "when",
    ] as const) {
      const value = raw[key];
      if (typeof value === "string" && value) out[key] = value;
    }
    // POP-01 (ADR-0229): as the stock lists above.
    if (typeof raw["open"] === "string" && raw["open"]) out.open = raw["open"];
    if (typeof raw["tab"] === "string" && raw["tab"]) out.tab = raw["tab"];
    return out;
  };

  const arrivalsListRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/arrivals",
    validateSearch: arrivalListSearch,
    component: ArrivalListPage,
  });

  const shortDeliveriesRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/short-deliveries",
    component: ShortDeliveriesPage,
  });

  const arrivalCountRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/arrivals/$id/count",
    component: ArrivalCountPage,
  });

  const arrivalDetailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "inventory/arrivals/$id",
    component: ArrivalStoryDetail,
  });
  const arrivalIndexRoute = createRoute({
    getParentRoute: () => arrivalDetailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/inventory/arrivals/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });
  // Named, not inline: the cockpit's route-integrity check reads `const x =
  // createRoute(...)` declarations, so a tab declared inline is invisible to
  // it and every link to it reads as dead.
  const arrivalOverviewRoute = createRoute({
    getParentRoute: () => arrivalDetailRoute,
    path: "overview",
    component: ArrivalOverviewRoute,
  });
  const arrivalLinesRoute = createRoute({
    getParentRoute: () => arrivalDetailRoute,
    path: "lines",
    component: ArrivalLinesRoute,
  });
  const arrivalRelatedRoute = createRoute({
    getParentRoute: () => arrivalDetailRoute,
    path: "related",
    component: ArrivalRelatedRoute,
  });
  const arrivalActivityRoute = createRoute({
    getParentRoute: () => arrivalDetailRoute,
    path: "activity",
    component: ArrivalActivityRoute,
  });
  (
    arrivalDetailRoute as unknown as { addChildren: (c: unknown) => void }
  ).addChildren([
    arrivalIndexRoute,
    arrivalOverviewRoute,
    arrivalLinesRoute,
    arrivalRelatedRoute,
    arrivalActivityRoute,
  ]);

  return [
    stockRoute,
    batchesRoute,
    receivingListRoute,
    receivingCreateRoute,
    adjustmentsListRoute,
    transfersListRoute,
    adjustmentDetailRoute,
    transferDetailRoute,
    arrivalsListRoute,
    shortDeliveriesRoute,
    arrivalCountRoute,
    arrivalDetailRoute,
  ];
};

/** The Arrivals queue's URL-borne filters (ADR-0081 §3). */
export interface ArrivalListSearch {
  state?: string;
  q?: string;
  supplier_id?: string;
  warehouse?: string;
  when?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

/** The list state a story page carries back (ADR-0081 §3). */
export interface StockListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  when?: string;
  product_id?: string;
  /** Adjustments. */
  site_id?: string;
  account_source_ref?: string;
  /** Transfers. */
  from_site_id?: string;
  to_site_id?: string;
  needs_attention?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

/** Batch list search params (ADR-0122): which batch sheet is open and which of
 *  its tabs. `batch_tab` is absent for the default Overview tab. */
export interface BatchListSearch {
  highlight?: string;
  batch_tab?: "related" | "activity";
}
